import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RulePreviewComponent } from '../rule-preview/rule-preview.component';
import { LabelLayoutStyle } from '../rule-preview/label-layout.helper';
import { TooltipWrapperComponent } from '../tooltip-wrapper/tooltip-wrapper.component';
import { ItemDesignerDialogComponent } from '../item-designer-dialog/item-designer-dialog.component';
import { DropPreviewService, DROP_PREVIEW_AREA_LEVEL_MAX } from '../../services/drop-preview.service';
import { RandomLootService, RandomLootSettings } from '../../services/random-loot.service';
import { DropPreviewSectionVisibility } from '../../services/drop-preview.models';
import { SettingsService } from '../../services/settings.service';
import { GameItem } from '../../models/game-item.models';
import { poe2 } from '../../../game';
import {
  getLabelPreviewBgSmallUrl,
  LABEL_PREVIEW_BACKGROUNDS,
  labelPreviewBgThumbStyle,
} from '../label-preview-backgrounds';

@Component({
  selector: 'app-drop-preview',
  imports: [RulePreviewComponent, TooltipWrapperComponent, ItemDesignerDialogComponent, FormsModule],
  templateUrl: './drop-preview.component.html',
  styleUrl: './drop-preview.component.scss',
})
export class DropPreviewComponent implements OnInit {
  readonly dropPreviewService = inject(DropPreviewService);
  readonly randomLootService = inject(RandomLootService);
  private readonly settingsService = inject(SettingsService);

  readonly poe2 = poe2;
  readonly areaLevelMax = DROP_PREVIEW_AREA_LEVEL_MAX;

  initialSections = input<DropPreviewSectionVisibility | null>(null);
  resizable = input(false);
  scrollable = input(false);
  compact = input(false);
  largeBackground = input(true);
  narrow = input(false);
  fixedLabelLayout = input<LabelLayoutStyle | null>(null);
  randomLootSettingsOverride = input<RandomLootSettings | null>(null);
  showLayoutToggle = input(true);
  showHoldAltToggle = input(true);
  showModeToggles = input(true);
  showBeamsToggle = input(true);
  showGlowToggle = input(true);
  showAreaLevelInput = input(false);
  showBackgroundToggle = input(true);

  readonly backgroundOptions = LABEL_PREVIEW_BACKGROUNDS.map(bg => ({
    ...bg,
    thumbStyle: labelPreviewBgThumbStyle(getLabelPreviewBgSmallUrl(bg.id)),
  }));

  readonly holdAlt = signal(false);
  readonly itemDesignerOpen = signal(false);
  readonly itemDesignerCloneItem = signal<GameItem | undefined>(undefined);
  areaLevelInput: number | '' = '';

  readonly libraryModeActive = computed(
    () => !!this.dropPreviewService.globalSectionVisibility()['itemLibrary'],
  );
  readonly randomLootModeActive = computed(
    () => !!this.dropPreviewService.globalSectionVisibility()['randomLoot'],
  );
  readonly randomLootRegenerateDisabled = computed(() => this.randomLootService.generateDisabled());
  readonly randomLootGenerating = computed(() => this.randomLootService.generating());
  readonly randomLootTooltip = computed(() => {
    if (this.randomLootGenerating()) {
      return 'Generating random loot…';
    }
    if (this.randomLootModeActive()) {
      return 'Click to regenerate random loot';
    }
    return 'Show Random Loot and regenerate';
  });
  readonly dropTiersModeActive = computed(() => this.dropPreviewService.anyDropTiersVisible());
  readonly glowToggleOff = computed(() => {
    const forceGlow = this.dropPreviewService.forceGlow();
    return forceGlow === false
      || (forceGlow === null && this.settingsService.labelGlow() !== 'always');
  });
  readonly labelLayoutStyle = computed(
    () => this.fixedLabelLayout() ?? this.dropPreviewService.labelLayoutStyle(),
  );
  readonly layoutToggleVisible = computed(
    () => this.showLayoutToggle() && this.fixedLabelLayout() === null,
  );

  readonly visiblePreview = computed(() => {
    this.dropPreviewService.ruleGroups();
    this.dropPreviewService.globalSectionVisibility();
    return this.dropPreviewService.resolveVisibleGroups();
  });

  readonly rules = computed(() => this.visiblePreview().rules);
  readonly ruleGroups = computed(() => this.visiblePreview().ruleGroups);

  constructor() {
    effect(() => {
      const request = this.dropPreviewService.itemDesignerRequest();
      if (request === null) return;
      this.itemDesignerCloneItem.set(request.item);
      this.itemDesignerOpen.set(true);
    });

    effect(() => {
      const level = this.dropPreviewService.areaLevel();
      this.areaLevelInput = level ?? '';
    });
  }

  ngOnInit(): void {
    const initial = this.initialSections();
    if (initial) {
      this.dropPreviewService.initializeSections(initial);
    }
  }

  toggleHoldAlt(): void {
    this.holdAlt.update(v => !v);
  }

  selectLibraryMode(): void {
    this.dropPreviewService.showOnlyItemLibrary();
  }

  selectRandomLootMode(): void {
    if (this.randomLootRegenerateDisabled()) return;
    const overrides = this.randomLootSettingsOverride() ?? undefined;
    if (this.randomLootModeActive()) {
      void this.randomLootService.generateLootPile(overrides);
      return;
    }
    this.dropPreviewService.showOnlyRandomLoot();
    void this.randomLootService.generateLootPile(overrides);
  }

  selectDropTiersMode(): void {
    this.dropPreviewService.showOnlyDropTiers();
  }

  openItemDesigner(): void {
    this.dropPreviewService.openItemDesigner();
  }

  closeItemDesigner(): void {
    this.itemDesignerOpen.set(false);
    this.itemDesignerCloneItem.set(undefined);
    this.dropPreviewService.clearItemDesignerRequest();
  }

  onAreaLevelInputChange(): void {
    const raw = this.areaLevelInput;
    if (raw === '' || raw === null || raw === undefined) {
      this.dropPreviewService.setAreaLevel(null);
      return;
    }

    const parsed = Math.round(Number(raw));
    if (!Number.isFinite(parsed)) {
      const level = this.dropPreviewService.areaLevel();
      this.areaLevelInput = level ?? '';
      return;
    }

    const areaLevel = Math.min(this.areaLevelMax, Math.max(1, parsed));
    this.dropPreviewService.setAreaLevel(areaLevel);
    this.areaLevelInput = areaLevel;
  }
}
